// File: packages/components/src/homepage/impact/ImpactTeaserSection.tsx
import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Button } from '@dopomogai/ui';
import { HeartHandshake } from 'lucide-react';

type Accent =
  | 'emerald' | 'violet' | 'blue' | 'cyan' | 'amber' | 'rose'
  | 'indigo' | 'purple' | 'teal' | 'orange' | 'lime' | 'fuchsia'
  | 'pink' | 'slate' | 'gray';

const accentClasses = (accent: Accent = 'emerald') => {
  switch (accent) {
    case 'violet':  return { badgeBg: 'bg-violet-100', dot: 'bg-violet-600' };
    case 'blue':    return { badgeBg: 'bg-blue-100', dot: 'bg-blue-600' };
    case 'cyan':    return { badgeBg: 'bg-cyan-100', dot: 'bg-cyan-600' };
    case 'amber':   return { badgeBg: 'bg-amber-100', dot: 'bg-amber-600' };
    case 'rose':    return { badgeBg: 'bg-rose-100', dot: 'bg-rose-600' };
    case 'indigo':  return { badgeBg: 'bg-indigo-100', dot: 'bg-indigo-600' };
    case 'purple':  return { badgeBg: 'bg-purple-100', dot: 'bg-purple-600' };
    case 'teal':    return { badgeBg: 'bg-teal-100', dot: 'bg-teal-600' };
    case 'orange':  return { badgeBg: 'bg-orange-100', dot: 'bg-orange-600' };
    case 'lime':    return { badgeBg: 'bg-lime-100', dot: 'bg-lime-600' };
    case 'fuchsia': return { badgeBg: 'bg-fuchsia-100', dot: 'bg-fuchsia-600' };
    case 'pink':    return { badgeBg: 'bg-pink-100', dot: 'bg-pink-600' };
    case 'slate':   return { badgeBg: 'bg-slate-100', dot: 'bg-slate-700' };
    case 'gray':    return { badgeBg: 'bg-gray-100', dot: 'bg-gray-700' };
    default:        return { badgeBg: 'bg-emerald-100', dot: 'bg-emerald-600' };
  }
};

export type ImpactTeaserSectionProps = {
  title?: string;
  titleKey?: string;
  text?: string;
  textKey?: string;

  bullets?: string[];
  bulletKeys?: string[];

  // Visual controls
  accent?: Accent;
  badgeText?: string;
  badgeTextKey?: string;
  align?: 'left' | 'center';

  cta?: { label?: string; labelKey?: string; actionId: string; variant?: 'primary' | 'secondary' | 'outline' | 'ghost' };
  onAction?: (actionId: string, payload?: any) => void;

  className?: string;
};

export const ImpactTeaserSection: React.FC<ImpactTeaserSectionProps> = ({
  title,
  titleKey,
  text,
  textKey,
  bullets = [],
  bulletKeys = [],
  accent = 'emerald',
  badgeText,
  badgeTextKey,
  align = 'left',
  cta,
  onAction,
  className,
}) => {
  const { t } = useTranslation('homepage');

  const resolvedTitle = titleKey ? t(titleKey) : title;
  const resolvedText = textKey ? t(textKey) : text;
  const resolvedBadgeText = badgeTextKey ? t(badgeTextKey) : (badgeText ?? '33% to non‑profits');
  const resolvedBullets = useMemo(
    () => (bullets.length ? bullets : bulletKeys.map((k) => t(k))).filter(Boolean),
    [bullets, bulletKeys, t]
  );

  const ac = accentClasses(accent);
  const centered = align === 'center';

  return (
    <section className={clsx('bg-slate-50', className)}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div
          className={clsx(
            'relative border-2 border-black rounded-3xl bg-white px-6 md:px-10 py-8 md:py-12',
            'transition-all duration-200 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)]'
          )}
        >
          {/* Eyebrow badge */}
          <div className={clsx('mb-6', centered && 'text-center')}>
            <span className={clsx('inline-flex items-center gap-2 border-2 border-black rounded-xl px-3 py-2', ac.badgeBg)}>
              <HeartHandshake size={20} className="text-slate-800" aria-hidden="true" />
              <span className="text-sm font-semibold text-slate-900">{resolvedBadgeText}</span>
            </span>
          </div>

          {/* Copy */}
          <div className={clsx(centered && 'text-center')}>
            {resolvedTitle ? (
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
                {resolvedTitle}
              </h2>
            ) : null}
            {resolvedText ? (
              <p className="mt-4 text-slate-700 text-lg md:text-xl">
                {resolvedText}
              </p>
            ) : null}
          </div>

          {/* Bullets */}
          {resolvedBullets.length > 0 && (
            <ul className={clsx('mt-5 md:mt-6 space-y-3', centered && 'mx-auto max-w-3xl text-left')}>
              {resolvedBullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-800 text-lg">
                  <span className={clsx('mt-2 inline-block w-2.5 h-2.5 rounded-full', ac.dot)} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {/* CTA */}
          {cta ? (
            <div className={clsx('mt-8', centered ? 'flex justify-center' : '')}>
              <Button
                variant={cta.variant ?? 'secondary'}
                size="lg"
                text={cta.labelKey ? t(cta.labelKey) : (cta.label ?? 'Learn about our Impact')}
                onClick={() => onAction?.(cta.actionId)}
              />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default ImpactTeaserSection;
